import React from "react";
import styled from "styled-components/macro";
import ScrollAreaCard from "components/shared/ScrollAreaCard";
import { COLORS, FONTSIZE, FONTWEIGHT, CONSTANTS } from "helpers/designConstants";
import { useTranslation } from "react-i18next";
import { MiniButton } from "components/shared/standardElements";

const countGroups = (sections) => {
  if (!sections) return 0;
  return sections.reduce(
    (sum, s) => sum + 1 + (s.groups ? countGroups(s.groups) : 0),
    0
  );
};

const CatalogueCard = ({
  catalogue,
  onEditArticlesForCatalogue,
  onEditCatalogueDetails,
  onCopyLink,
  onDeleteCatalogue,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const isCustom = catalogue.groupingMode === "custom";
  const groupCount = isCustom ? countGroups(catalogue.customSections) : 0;
  const articleCount = catalogue.articleIDs?.length || 0;

  return (
    <ScrollAreaCard {...props}>
      <Content>
        <TopRow>
          <Name>{catalogue.name}</Name>
          <ModeBadge $custom={isCustom}>
            {isCustom
              ? t("custom") || "Custom"
              : t("auto") || "Auto"}
          </ModeBadge>
        </TopRow>
        <MetaRow>
          <CreationDate>{`${t("created_on")}: ${new Date(
            catalogue.creationDate
          ).toLocaleString(i18n.language, {
            month: "short",
            day: "numeric",
            year: "numeric"
          })}`}</CreationDate>
          <Stats>
            {isCustom && groupCount > 0 && (
              <span>{groupCount} {t("groups") || "groups"}</span>
            )}
            {isCustom && groupCount > 0 && articleCount > 0 && (
              <span> · </span>
            )}
            {articleCount > 0 && (
              <span>{articleCount} {t("articles") || "articles"}</span>
            )}
          </Stats>
        </MetaRow>
        <ButtonGroup>
          <MiniButton onClick={() => onEditArticlesForCatalogue(catalogue)}>
            {t("edit_articles_button")}
          </MiniButton>
          <MiniButton onClick={() => onEditCatalogueDetails(catalogue)}>
            {t("edit_details")}
          </MiniButton>
          <MiniButton destructive onClick={() => onDeleteCatalogue(catalogue)}>
            {t("delete_button")}
          </MiniButton>
          <MiniButton highlightedOutline onClick={() => onCopyLink(catalogue)}>
            {t("copy_link_button")}
          </MiniButton>
        </ButtonGroup>
      </Content>
    </ScrollAreaCard>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  color: ${COLORS.text};
  padding: 5px 10px;
  text-transform: uppercase;
  cursor: pointer;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Name = styled.div`
  font-size: ${FONTSIZE.medium};
  text-transform: uppercase;
  font-weight: ${FONTWEIGHT.heavy};
`;

const ModeBadge = styled.div`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: ${CONSTANTS.borderRadius};
  border: 1px solid ${COLORS.secondary};
  color: ${COLORS.text};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0 0;
`;

const CreationDate = styled.div`
  text-transform: uppercase;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
`;

const Stats = styled.div`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.text};
`;

const ButtonGroup = styled.div`
  border-top: 1px solid ${COLORS.secondary};
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  padding-top: 10px;

  ${MiniButton} {
    margin: 10px;
  }
`;

export default CatalogueCard;
